import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { LoginForm } from "../components/LoginForm";
import { ZohoAuthCard } from "../components/ZohoAuthCard";
import { useToast } from "../../../../shared/presentation/components";

export const LoginPage: React.FC = () => {
  const {
    login,
    loading, // Keep for initial auth check
    loginLoading, // Use for login action
    loginError, // Use for login errors
    clearLoginError,
    isAuthenticated,
    needsZohoAuth,
    user,
  } = useAuth();
  const navigate = useNavigate();
  const { showError } = useToast();

  // Show API error message (e.g. "Please access via Master Admin...") in error toast
  useEffect(() => {
    if (loginError) {
      showError(loginError);
    }
  }, [loginError, showError]);

  // Redirect authenticated users based on their role
  useEffect(() => {
    if (isAuthenticated && !needsZohoAuth()) {
      // Check if user has client_view role
      if (user?.role?.toLowerCase() === "client_view") {
        navigate("/client-overview");
      } else {
        navigate("/dashboard");
      }
    }
  }, [isAuthenticated, needsZohoAuth, navigate, user]);

  // Show loading while checking authentication (initial load only)
  if (loading && !isAuthenticated) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="loading-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  const handleLogin = async (email: string, password: string) => {
    clearLoginError();
    const result = await login({ email, password });

    if (result.success) {
      // Check if user needs Zoho authentication
      if (result.needsZohoAuth) {
        // Stay on this page to show Zoho auth
        return;
      }
      // The useEffect above will handle the redirect based on role
      // User state will be updated after login, triggering the useEffect
    }
  };

  // If user is authenticated but needs Zoho auth, show Zoho auth screen
  // Skip Zoho auth for client_view role users
  if (
    isAuthenticated &&
    needsZohoAuth() &&
    user?.role?.toLowerCase() !== "client_view"
  ) {
    return (
      <div className="flex h-screen w-full flex-col">
        <div className="flex h-full w-full flex-wrap items-start bg-default-background mobile:flex-col mobile:flex-wrap mobile:gap-0">
          <div className="flex max-w-[576px] grow shrink-0 basis-0 flex-col items-center gap-12 self-stretch bg-neutral-800 px-12 py-12 mobile:h-auto mobile:w-full mobile:flex-none">
            <div className="flex w-full max-w-[448px] grow shrink-0 basis-0 flex-col items-start justify-center gap-12 mobile:h-auto mobile:w-full mobile:max-w-[448px] mobile:flex-none">
              {/* <img
                alt={LOGIN_LOGO_ALT}
                className="h-8 flex-none object-cover invert"
                src={LOGIN_LOGO_PATH}
              /> */}
              <div className="flex flex-col items-start justify-center gap-16 pb-32 mobile:px-0 mobile:py-0">
                <div className="flex flex-col items-start gap-2">
                  <span className="text-heading-2 font-heading-2 text-white">
                    Welcome back
                  </span>
                  <span className="text-heading-3 font-heading-3 text-brand-200">
                    Complete your Zoho authentication to continue.
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex grow shrink-0 basis-0 flex-col items-center justify-center gap-6 self-stretch border-l border-solid border-neutral-border px-12 py-12">
            <div className="flex w-full max-w-[448px] flex-col items-start justify-center gap-8">
              <ZohoAuthCard />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen w-full flex-col">
      <div className="flex h-full w-full flex-wrap items-start bg-default-background mobile:flex-col mobile:flex-wrap mobile:gap-0">
        {/* Left Side - Branding */}
        <div className="flex max-w-[576px] grow shrink-0 basis-0 flex-col items-center gap-12 self-stretch bg-neutral-800 px-12 py-12 mobile:h-auto mobile:w-full mobile:flex-none">
          <div className="flex w-full max-w-[448px] grow shrink-0 basis-0 flex-col items-start justify-center gap-12 mobile:h-auto mobile:w-full mobile:max-w-[448px] mobile:flex-none">
            {/* <img
              alt={LOGIN_LOGO_ALT}
              className="h-8 flex-none object-cover invert"
              src={LOGIN_LOGO_PATH}
            /> */}
            <div className="flex flex-col items-start justify-center gap-16 pb-32 mobile:px-0 mobile:py-0">
              <div className="flex flex-col items-start gap-2">
                <span className="text-heading-2 font-heading-2 text-white">
                  Welcome back
                </span>
                <span className="text-heading-3 font-heading-3 text-brand-200">
                  Sign in to access your financial compliance dashboard.
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="flex grow shrink-0 basis-0 flex-col items-center justify-center gap-6 self-stretch border-l border-solid border-neutral-border px-12 py-12">
          <LoginForm
            onSubmit={handleLogin}
            isLoading={loginLoading}
            error={loginError}
          />
        </div>
      </div>
    </div>
  );
};
